import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQuery } from "@/redux/baseQuery";
import {
  BulkImportResponse,
  BulkImportRetryRow,
  BulkImportSubmitRow,
  BulkValidateResponse,
  BulkBookingInputRow,
  ClientBooking,
  ClientBookingData,
  ClientBookingDetail,
  ClientBookingDocument,
  ClientBookingDocumentsRawResponse,
  ClientBookingQuote,
  ClientBookingRequestArgs,
  CreateClientBookingResponse,
  GetClientBookingsArgs,
  GetClientBookingsRawResponse,
  GetClientBookingsResult,
  RawClientBooking,
  RawClientJobData,
} from "./types";

/**
 * Normalise the v3 job-data entity onto the shared ClientBookingData contract. The entity keys
 * vehicles under `vehicle_categories`, prices extras with `cost`, and names the job-rate `rate` /
 * `is_special_event`; mapping them to `vehicles` / `amount` / `type` / `special_event` keeps
 * VehicleTransferPanel and buildBookingPayload unchanged. The numeric ids preserved here (transfer
 * `id`, `vehicle_id`, `extra_id`) and `job_rate` (= `type`) keep the /v2/cust/quote contract intact.
 */
export const transformClientJobData = (raw: RawClientJobData): ClientBookingData => ({
  transfer_types: (raw?.transfer_types ?? []).map((t) => ({
    id: t.id,
    uuid: t.uuid,
    label: t.label,
    type: t.rate ?? "",
    special_event: t.is_special_event ?? false,
    vehicle_exclusions: t.vehicle_exclusions ?? [],
  })),
  vehicles: (raw?.vehicle_categories ?? []).map((v) => ({
    vehicle_id: v.vehicle_id,
    uuid: v.uuid,
    name: v.name,
    max_passengers: v.max_passengers,
    max_bags: v.max_bags,
  })),
  extras: (raw?.extras ?? []).map((e) => ({
    // ClientBookingData keys extras as strings (VehicleTransferPanel's extraIds is string[]).
    extra_id: String(e.extra_id),
    name: e.name,
    amount: e.cost,
  })),
  hourly_rates: raw?.hourly_rates ?? [],
  daily_rates: raw?.daily_rates ?? [],
  cancellation_policy: raw?.cancellation?.policy ? { text: raw.cancellation.policy.text, fees: raw.cancellation.policy.fees } : undefined,
  max_waypoints: raw?.max_waypoints,
});

/** Map one raw HAL booking to the normalised UI shape (shared by the self + managed lists). */
const toClientBookingItem = (item: RawClientBooking): ClientBooking => {
  const pickupWp = item.waypoints?.[0];
  const destWp = item.waypoints?.[item.waypoints.length - 1];
  const pickup = pickupWp?.formatted_address || pickupWp?.address || pickupWp?.city || "";
  const destination = destWp?.formatted_address || destWp?.address || destWp?.city || "";

  return {
    jobId: item.job_id,
    displayJobId: item.display_job_id ?? `J${String(item.job_id).padStart(6, "0")}`,
    status: item.job_status,
    statusReason: item.job_status_reason,
    pickupTime: item.pickup_time,
    pickupTimezone: item.pickup_timezone,
    commencementDate: item.commencement_date,
    completionDate: item.completion_date,
    total: item.total,
    estimatedPayment: item.estimated_payment,
    vehicleTotal: item.vehicle?.total ?? null,
    passengerName: `${item.passenger?.first_name ?? ""} ${item.passenger?.last_name ?? ""}`.trim(),
    pickupLocation: pickup,
    destination: pickup !== destination ? destination : "",
    flags: item.flags,
    // Flight provenance for the pickup-time caption in Booking History. The list endpoint carries the
    // whole flight block, so no extra fetch is needed.
    pickupDelayMinutes: item.flight_number ? item.flight_pickup_delay_minutes : null,
    derivedArrival: item.flight_pickup_derived_arrival,
    // Only the managed list carries the owning client (scope=managed).
    managedAccount: item.managed_account,
  };
};

/** Build the /v2/cust/booking list params shared by the self + managed queries. */
const buildClientBookingParams = ({
  page,
  pageSize,
  past = true,
  status,
  search,
  startDate,
  endDate,
  sort,
  order,
  passenger,
  state,
}: GetClientBookingsArgs) => ({
  page,
  page_size: pageSize,
  // `status` (e.g. "all") supersedes `past` — DEV-1251/DEV-1243.
  ...(status ? { status } : { past: past ? "1" : undefined }),
  ...(search ? { search } : {}),
  ...(startDate ? { start_date: startDate } : {}),
  ...(endDate ? { end_date: endDate } : {}),
  ...(sort ? { sort, order: order ?? "asc" } : {}),
  ...(passenger ? { passenger } : {}),
  ...(state?.length ? { state: state.join(",") } : {}),
});

/** Normalise the /v2/cust/booking list envelope into the UI result shape (self + managed). */
const toClientBookingsResult = (response: GetClientBookingsRawResponse): GetClientBookingsResult => ({
  items: (response?._embedded?.booking ?? response?._embedded?.items ?? []).map(toClientBookingItem),
  totalItems: response?.total_items ?? 0,
  pageCount: response?.page_count ?? 1,
  page: response?.page ?? 1,
});

export const clientBookingApi = createApi({
  reducerPath: "clientBookingApi",
  baseQuery,
  tagTypes: ["ClientBookings", "ClientBookingDocuments", "BulkImport"],
  endpoints: (builder) => ({
    getClientBookings: builder.query<GetClientBookingsResult, GetClientBookingsArgs>({
      query: (args) => ({
        url: "/v2/cust/booking",
        // Self-service omits account_uuid; under OBO (EA/operator) it scopes to the managed
        // account's bookings (DEV-1299).
        params: { ...buildClientBookingParams(args), ...(args.accountId ? { account_uuid: args.accountId } : {}) },
      }),
      transformResponse: toClientBookingsResult,
      providesTags: ["ClientBookings"],
    }),
    // EA managed list (All Bookings landing) — GET /v2/cust/booking?scope=managed. Same list params as
    // the self endpoint (no account_uuid); the backend merges bookings across every client the assistant
    // manages and stamps each with its owning `managed_account`. Result shape matches the self list,
    // plus the per-row `managedAccount` (surfaced as the extra "Client" column).
    getManagedBookings: builder.query<GetClientBookingsResult, GetClientBookingsArgs>({
      query: (args) => ({
        url: "/v2/cust/booking",
        params: { ...buildClientBookingParams(args), scope: "managed" },
      }),
      transformResponse: toClientBookingsResult,
      providesTags: ["ClientBookings"],
    }),
    // Single-booking detail (DEV-1253) — GET /v2/cust/booking/{jobId}. The list item is too thin to
    // rebuild a booking (display strings + bare waypoints); this fetch adds the numeric vehicle id,
    // full waypoints and passenger/bag counts that Replicate maps into a re-book draft. Lazy: fetched
    // on demand when the user hits "Replicate", not for every visible row.
    getClientBooking: builder.query<ClientBookingDetail, { jobId: number; accountId?: string }>({
      // Under operator OBO the managed client's account_uuid scopes the fetch — the backend resolves it
      // as the domainAccount, so the booking's owner check passes (BookingResource::fetch). Self-service
      // omits it and reads its own booking. Query param, mirroring quote/create/update.
      query: ({ jobId, accountId }) => ({
        url: `/v2/cust/booking/${jobId}`,
        method: "GET",
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      // Tag per jobId so update/cancel (which invalidate the general "ClientBookings" tag) also refetch
      // THIS booking's detail — otherwise re-opening a just-edited booking replays stale cached values.
      providesTags: (_r, _e, { jobId }) => [{ type: "ClientBookings", id: jobId }],
    }),
    // Booking reference data for the Vehicle & Transfer step. The v3 route scopes the account in the
    // PATH (not a query param), so there's no RM-API-1005 admin-API workaround: the caller
    // (useBookingData) resolves self-service (own account) vs operator OBO (managed client).
    getClientBookingData: builder.query<ClientBookingData, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/v3/client/${accountId}/job/data`,
        method: "GET",
      }),
      transformResponse: transformClientJobData,
    }),
    // Priced quote for the current draft — same payload as createClientBooking
    // (one builder: buildBookingPayload). A query (not mutation) so the summary
    // re-quotes automatically as the draft changes; POST is the verb the v2 API exposes.
    getClientBookingQuote: builder.query<ClientBookingQuote, ClientBookingRequestArgs>({
      query: ({ payload, accountId }) => ({
        url: "/v2/cust/quote",
        method: "POST",
        body: payload,
        // OBO: `account_uuid` as a query param (wins over body server-side); self-service omits it.
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
    }),
    createClientBooking: builder.mutation<CreateClientBookingResponse, ClientBookingRequestArgs>({
      query: ({ payload, accountId }) => ({
        url: "/v2/cust/booking",
        method: "POST",
        body: payload,
        params: accountId ? { account_uuid: accountId } : undefined,
        // Success is 201; only an explicit success:false is a failure. Not requiring a truthy
        // `success` avoids surfacing a real 201 (body may omit it) as an error, which would
        // prompt a retry and double-book. But a server-side form-validation failure returns
        // `{ errors, validation, invalidInput: true }` — also a 201, with no `job_id`. Treat that
        // as a failure so it doesn't sail through as success (which navigated to a confirmation for
        // a booking that never existed and POSTed attachments to /booking/undefined/document).
        validateStatus: (response, result: CreateClientBookingResponse & { invalidInput?: boolean }) =>
          response.status === 201 && result?.success !== false && !result?.invalidInput,
      }),
      invalidatesTags: ["ClientBookings"],
      // Mobile parity: guarantee an error `detail` so the UI always has a message to show. Prefer the
      // server's own `errors` string (e.g. the validation-failure body) over the generic fallback.
      transformErrorResponse: (response) => {
        const data = response.data as { detail?: string; errors?: string } | undefined;
        if (data?.detail) return response;
        return {
          ...response,
          data: {
            ...(response.data as object),
            detail: data?.errors ?? "Unable to create booking. Please check all fields and try again.",
          },
        };
      },
    }),
    // Edit an existing booking — PUT /v2/cust/booking/{jobId}. Same payload contract as create
    // (buildBookingPayload); the backend routes it through BookingResource::update()->process() and
    // rejects the edit server-side (400) if the job is no longer editable. Invalidates the list so the
    // updated row re-fetches. Mirrors createClientBooking's success/validation + error-detail handling.
    updateClientBooking: builder.mutation<CreateClientBookingResponse, ClientBookingRequestArgs & { jobId: number }>({
      query: ({ jobId, payload, accountId }) => ({
        url: `/v2/cust/booking/${jobId}`,
        method: "PUT",
        body: payload,
        params: accountId ? { account_uuid: accountId } : undefined,
        validateStatus: (response, result: CreateClientBookingResponse & { invalidInput?: boolean }) =>
          (response.status === 200 || response.status === 201) && result?.success !== false && !result?.invalidInput,
      }),
      invalidatesTags: ["ClientBookings"],
      transformErrorResponse: (response) => {
        const data = response.data as { detail?: string; errors?: string } | undefined;
        if (data?.detail) return response;
        return {
          ...response,
          data: {
            ...(response.data as object),
            detail: data?.errors ?? "Unable to update booking. Please check all fields and try again.",
          },
        };
      },
    }),
    // Cancel a booking — POST /v2/cust/booking/cancel/:jobId (CancelResource). Body carries the job_id;
    // the backend validates ownership + editability and rejects special-event jobs. Invalidates the
    // list so the row's status flips to Cancelled on refetch.
    cancelClientBooking: builder.mutation<{ success?: boolean }, { jobId: number; accountId?: string }>({
      query: ({ jobId, accountId }) => ({
        url: `/v2/cust/booking/cancel/${jobId}`,
        method: "POST",
        body: { job_id: jobId },
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      invalidatesTags: ["ClientBookings"],
      transformErrorResponse: (response) => {
        const data = response.data as { detail?: string; errors?: string } | undefined;
        if (data?.detail) return response;
        return {
          ...response,
          data: { ...(response.data as object), detail: data?.errors ?? "Unable to cancel this booking. Please try again." },
        };
      },
    }),
    // Email the payment tax invoice for a booking — POST /v2/cust/booking/{jobId}/email-invoice
    // (EmailInvoiceResource). The backend re-sends the same job_invoice email(s) the legacy portal's
    // "Email My Invoice" action did (one per successful payment attempt) to the account's payment
    // recipients. Nothing in the list changes, so no tag invalidation. Mirrors cancel's error-detail shape.
    emailInvoiceClientBooking: builder.mutation<{ success?: boolean }, { jobId: number; accountId?: string }>({
      query: ({ jobId, accountId }) => ({
        url: `/v2/cust/booking/${jobId}/email-invoice`,
        method: "POST",
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      transformErrorResponse: (response) => {
        const data = response.data as { detail?: string; errors?: string } | undefined;
        if (data?.detail) return response;
        return {
          ...response,
          data: { ...(response.data as object), detail: data?.errors ?? "Unable to send invoice. Please try again." },
        };
      },
    }),
    // Upload one attachment to a created booking's job (client `cust` document endpoint).
    // Runs after createClientBooking returns a job_id — the booking must exist first.
    uploadClientBookingAttachment: builder.mutation<void, { jobId: number; body: FormData; accountId?: string }>({
      query: ({ jobId, body, accountId }) => ({
        url: `/v2/cust/booking/${jobId}/document`,
        method: "POST",
        body,
        // OBO: `account_uuid` as a query param (mirrors createClientBooking); self-service omits it.
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      // A new upload changes the stored list — refresh it so view/edit shows the added file.
      invalidatesTags: (_r, _e, { jobId }) => [{ type: "ClientBookingDocuments", id: jobId }],
    }),
    // A booking's stored attachments (list). Feeds the view/edit sheet so existing files are shown.
    // The list omits the presigned `link` — fetch a single document (below) to download.
    getClientBookingDocuments: builder.query<ClientBookingDocument[], { jobId: number; accountId?: string }>({
      query: ({ jobId, accountId }) => ({
        url: `/v2/cust/booking/${jobId}/document`,
        method: "GET",
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      transformResponse: (response: ClientBookingDocumentsRawResponse) => response?._embedded?.document ?? [],
      providesTags: (_r, _e, { jobId }) => [{ type: "ClientBookingDocuments", id: jobId }],
    }),
    // Single document — the only response that carries a presigned `link` (2-min expiry). Fetched
    // lazily on a download click.
    getClientBookingDocument: builder.query<ClientBookingDocument, { jobId: number; uuid: string; accountId?: string }>({
      query: ({ jobId, uuid, accountId }) => ({
        url: `/v2/cust/booking/${jobId}/document/${uuid}`,
        method: "GET",
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
    }),
    // Delete a stored attachment. Under operator OBO the same `account_uuid` scoping applies. Fired by
    // the edit flow ONLY when the user commits via "Update Booking" (staged in the sheet until then).
    deleteClientBookingAttachment: builder.mutation<void, { jobId: number; uuid: string; accountId?: string }>({
      query: ({ jobId, uuid, accountId }) => ({
        url: `/v2/cust/booking/${jobId}/document/${uuid}`,
        method: "DELETE",
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      invalidatesTags: (_r, _e, { jobId }) => [{ type: "ClientBookingDocuments", id: jobId }],
    }),
    // --- Bulk booking (DEV-1215) ---
    // Check a chunk of spreadsheet rows: resolve addresses, match the provider's transfer/vehicle/
    // extra names, validate every field and price what's bookable. Writes NOTHING, so it's safe to
    // re-run — an inline edit re-validates just that row through the same call.
    validateBulkBooking: builder.mutation<
      BulkValidateResponse,
      { rows: BulkBookingInputRow[]; accountId?: string; paymentMethodUuid?: string }
    >({
      query: ({ rows, accountId, paymentMethodUuid }) => ({
        url: "/v2/cust/booking/bulk/validate",
        method: "POST",
        // The card prices the whole chunk: it decides the processing fee, and an international card
        // is surcharged at 3.5% against a domestic 1.7%. Omitted, the server uses the account default.
        body: { rows, payment_method_uuid: paymentMethodUuid },
        // OBO: operator and EA imports scope to the chosen client, as every other booking call does.
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
    }),
    /**
     * Queue an import. Returns as soon as the rows are written down — the booking happens in a
     * worker, so the tab only has to survive this one request.
     *
     * Each row carries its `source`: the plain spreadsheet values the payload was built from. The
     * payload is machine-shaped and there's no way back from it to something a person can edit, so
     * without the source a failed row could only ever be retried unchanged.
     */
    importBulkBooking: builder.mutation<
      BulkImportResponse,
      {
        rows: BulkImportSubmitRow[];
        accountId?: string;
        paymentMethodUuid?: string;
        fileName?: string;
      }
    >({
      query: ({ rows, accountId, paymentMethodUuid, fileName }) => ({
        url: "/v2/cust/booking/bulk",
        method: "POST",
        // Must be the card the rows were priced against, or the fare shown isn't the fare billed.
        // The file name is carried so a finished import can be recognised later by what produced it.
        body: { rows, payment_method_uuid: paymentMethodUuid, file_name: fileName },
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      // Newly booked trips must show up in the list behind the wizard.
      invalidatesTags: ["ClientBookings"],
    }),
    /**
     * Progress of a queued import. Polled while it runs — the work happens in a worker, so this is
     * the only way to see it, and it keeps reporting after the tab that started it has gone.
     *
     * Also how a finished import is read back days later, from the link in the completion email.
     */
    getBulkImport: builder.query<BulkImportResponse, { bulkId: string; accountId?: string }>({
      query: ({ bulkId, accountId }) => ({
        url: `/v2/cust/booking/bulk/${bulkId}`,
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      providesTags: (_r, _e, { bulkId }) => [{ type: "BulkImport", id: bulkId }],
    }),
    /**
     * Correct the rows that failed and run them again — one call, because they're one intention.
     *
     * Sending the fixes separately would leave a window where a retry could pick up the old details.
     * `rows` may be empty: retrying unchanged is still the right move for a failure that was
     * transient, like a worker that died mid-booking.
     */
    retryBulkImport: builder.mutation<BulkImportResponse, { bulkId: string; rows: BulkImportRetryRow[]; accountId?: string }>({
      query: ({ bulkId, rows, accountId }) => ({
        url: `/v2/cust/booking/bulk/${bulkId}`,
        method: "PATCH",
        body: { rows },
        params: accountId ? { account_uuid: accountId } : undefined,
      }),
      invalidatesTags: (_r, _e, { bulkId }) => [{ type: "BulkImport", id: bulkId }, "ClientBookings"],
    }),
  }),
});

export const {
  useValidateBulkBookingMutation,
  useImportBulkBookingMutation,
  useGetBulkImportQuery,
  useRetryBulkImportMutation,
  useGetClientBookingsQuery,
  useGetManagedBookingsQuery,
  useLazyGetClientBookingQuery,
  useGetClientBookingDataQuery,
  useLazyGetClientBookingDataQuery,
  useGetClientBookingQuoteQuery,
  useCreateClientBookingMutation,
  useUpdateClientBookingMutation,
  useCancelClientBookingMutation,
  useEmailInvoiceClientBookingMutation,
  useUploadClientBookingAttachmentMutation,
  useGetClientBookingDocumentsQuery,
  useLazyGetClientBookingDocumentQuery,
  useDeleteClientBookingAttachmentMutation,
} = clientBookingApi;
